import * as React from 'react';

import LiveViewTable, { DEFAULT_SORT, LiveViewSort } from './liveViewTable';
import { MayNeedHelpKey } from './mayNeedHelp';
import MayNeedHelpSettingsModal from './mayNeedHelpSettingsModal';
import { SchoolAdmin } from './premiumCallOut';
import RecentlyCompleted, { CompletedActivity } from './recentlyCompleted';
import { useLiveViewRows } from './useLiveViewRows';

import { requestGet } from '../../../../modules/request';
import { Checkbox, DropdownInput, SMALL_ICON_BASE_SRC, pluralize } from '../../../Shared/index';

const pencilImgSrc = `${SMALL_ICON_BASE_SRC}/pencil.svg`;

const ACTIVITY_FEED_URL = '/teachers/activity_feed';
// A class often finishes together; wait for the burst to settle and fetch once
export const ACTIVITY_FEED_REFETCH_DELAY_MS = 2000;
// Counts move with every heartbeat; announce only once they settle
export const STATUS_ANNOUNCEMENT_DELAY_MS = 5000;

export const SELECTED_CLASSROOM_KEY = 'live-view-selected-classroom-id';
const ALL_CLASSES_OPTION = { value: null, label: 'All classes' };

// Storage can be missing or blocked, which just means all classes
const readSelectedClassroomId = (): number|null => {
  try {
    const saved = window.localStorage.getItem(SELECTED_CLASSROOM_KEY);
    return saved ? Number(saved) : null;
  } catch (e) {
    return null;
  }
};

const saveSelectedClassroomId = (classroomId: number|null) => {
  try {
    if (classroomId === null) { window.localStorage.removeItem(SELECTED_CLASSROOM_KEY); } else { window.localStorage.setItem(SELECTED_CLASSROOM_KEY, String(classroomId)); }
  } catch (e) {
    // ignore
  }
};

export const statusSummary = (studentCount: number, workingCount: number, needHelpCount: number): string => {
  const parts = [
    `${studentCount} ${pluralize(studentCount, 'student', 'students')} online`,
    `${workingCount} working on activities`,
  ];
  if (needHelpCount) { parts.push(`${needHelpCount} may need help`); }
  return `${parts.join(', ')}.`;
};

// The table changes without any action from the teacher, so screen reader users
// hear a summary when it settles rather than every individual update
const useStatusAnnouncement = (summary: string|null): string => {
  const [announcement, setAnnouncement] = React.useState('');

  React.useEffect(() => {
    if (summary === null) { return undefined; }

    const timeoutId = window.setTimeout(() => setAnnouncement(summary), STATUS_ANNOUNCEMENT_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [summary]);

  return announcement;
};

// [15  Students Online]: the count bold, two spaces, then the words
const HeaderCount = ({ count, singular, plural }: { count: number, singular: string, plural: string }) => (
  <span className="live-view-header-count">
    <b>{count}</b>{'\u00a0\u00a0'}{pluralize(count, singular, plural)}
  </span>
);

interface LiveViewProps {
  // School or District Premium: every student in full
  fullAccess: boolean,
  schoolAdmins?: SchoolAdmin[]|null,
}

const LiveView = ({ fullAccess, schoolAdmins, }: LiveViewProps) => {
  const [savedClassroomId, setSavedClassroomId] = React.useState(readSelectedClassroomId);
  const {
    rows, completedRows, students, studentCount, loading, now, completedCount, helpRules, defaultHelpRules, setHelpRuleOverride, classrooms, classroomId,
  } = useLiveViewRows({ details: true, classroomId: savedClassroomId });
  const classroomOptions = React.useMemo(() => [ALL_CLASSES_OPTION, ...classrooms.map(classroom => ({ value: classroom.id, label: classroom.name }))], [classrooms]);
  const selectClassroom = React.useCallback((option: { value: number|null }) => {
    setSavedClassroomId(option.value);
    saveSelectedClassroomId(option.value);
  }, []);
  const viewAllClassrooms = React.useCallback(() => selectClassroom(ALL_CLASSES_OPTION), [selectClassroom]);
  const [inProgressOnly, setInProgressOnly] = React.useState(false);
  // Kept here so the sort survives the table unmounting while it is empty
  const [sort, setSort] = React.useState<LiveViewSort>(DEFAULT_SORT);
  const [showSettings, setShowSettings] = React.useState(false);
  const openSettings = React.useCallback(() => setShowSettings(true), []);
  const closeSettings = React.useCallback(() => setShowSettings(false), []);
  const [activityFeed, setActivityFeed] = React.useState<CompletedActivity[]|null>(null);
  const activityFeedRequest = React.useRef(0);

  const fetchActivityFeed = React.useCallback(() => {
    activityFeedRequest.current += 1;
    const requestId = activityFeedRequest.current;
    const ignoreFailure = () => {};
    const url = classroomId === null ? ACTIVITY_FEED_URL : `${ACTIVITY_FEED_URL}?classroom_id=${classroomId}`;
    requestGet(url, (response) => {
      if (requestId === activityFeedRequest.current) { setActivityFeed(response.data); }
    }, ignoreFailure).catch(ignoreFailure);
  }, [classroomId]);

  React.useEffect(() => {
    setActivityFeed(null);
    fetchActivityFeed();
    return () => { activityFeedRequest.current += 1; };
  }, [fetchActivityFeed]);

  // Only a completion changes the feed, so refresh on those and nothing else
  React.useEffect(() => {
    if (!completedCount) { return undefined; }

    const timeoutId = window.setTimeout(fetchActivityFeed, ACTIVITY_FEED_REFETCH_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [completedCount, fetchActivityFeed]);

  const toggleInProgressOnly = React.useCallback(() => setInProgressOnly(current => !current), []);
  // Just-finished students show under either filter
  const tableRows = inProgressOnly ? [...rows, ...completedRows] : students;
  const emptyStatus = inProgressOnly ? 'in progress' : 'online';
  const emptyMessage = classroomId === null
    ? `No students are currently ${emptyStatus}.`
    : <React.Fragment>No students <span className="live-view-selected-class">in the selected class</span> are currently {emptyStatus}.</React.Fragment>;
  const needHelpCount = rows.filter(row => row.lowScore || row.tooLong).length;
  const announcement = useStatusAnnouncement(loading ? null : statusSummary(studentCount, rows.length, needHelpCount));

  return (
    <main className="live-view-container container">
      <div className="live-view-header">
        <div className="live-view-title-row">
          <h1><b>Live View</b></h1>
          <HeaderCount count={studentCount} plural="Students Online" singular="Student Online" />
          <HeaderCount count={rows.length} plural="In Activities" singular="In Activity" />
        </div>
        <p className="live-view-subtext">See your students&rsquo; live progress on their activities. Quill flags students who may be stuck and would benefit from your assistance.</p>
      </div>
      <MayNeedHelpKey locked={!fullAccess} rows={rows} />
      <div className="live-view-table-controls">
        <Checkbox label="Only Show In-Progress Students" onClick={toggleInProgressOnly} selected={inProgressOnly} />
        {classrooms.length > 1 && (
          <DropdownInput
            className="small bordered live-view-class-filter"
            handleChange={selectClassroom}
            id="live-view-class-filter"
            label="Class"
            options={classroomOptions}
            value={classroomOptions.find(option => option.value === classroomId)}
          />
        )}
        <button className="quill-button outlined small grey focus-on-light may-need-help-settings-button" disabled={!helpRules.length} onClick={openSettings} type="button">
          <img alt="" src={pencilImgSrc} />
          May Need Help Settings
        </button>
      </div>
      {loading ? <p className="live-view-loading">Loading…</p> : <LiveViewTable emptyAction={classroomId !== null && <button className="quill-button contained green medium focus-on-light" onClick={viewAllClassrooms} type="button">View all classrooms</button>} emptyMessage={emptyMessage} fullAccess={fullAccess} now={now} onSortChange={setSort} rows={tableRows} schoolAdmins={schoolAdmins} showcaseFirst sort={sort} />}
      {activityFeed && <RecentlyCompleted activities={activityFeed.filter(activity => classroomId === null || activity.classroom_id === classroomId)} />}
      {showSettings && <MayNeedHelpSettingsModal closeModal={closeSettings} defaultRules={defaultHelpRules} onSave={setHelpRuleOverride} rules={helpRules} />}
      <p aria-live="polite" className="sr-only" role="status">{announcement}</p>
    </main>
  );
};

export default LiveView;
